import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Dates } from '@app/analytics/metric-widget/query/outline/Dates';
import { QueryOutlineBuilder } from '@app/analytics/metric-widget/query/outline/QueryOutlineBuilder';
import { ComplexQueryOutline } from '@app/analytics/metric-widget/query/outline/ComplexQueryOutline';
import { DataType } from '@app/shared/models/sensor-node-data-type';
import { IQueryContext } from '@app/analytics/metric-widget/query/context';
import { defaultContext } from '@app/analytics/metric-widget/query/query-outline-builder-factory';
import { LiveQueryOutline } from '@app/analytics/metric-widget/query/outline/LiveQueryOutline';
import { Resolution } from '@app/analytics/metric-widget/data-objects/resolution';
import { ActivatedRoute, Params, Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsMetricsService {
  private metricContext = new BehaviorSubject<IQueryContext>(defaultContext);

  constructor(private readonly router: Router, private route: ActivatedRoute) {}

  get currentContext$(): Observable<IQueryContext> {
    return this.metricContext.asObservable();
  }

  get currentContext(): IQueryContext {
    return this.metricContext.value;
  }

  set tagContext(tagIds: number[]) {
    const context = this.metricContext.value;
    context.tagIds = tagIds;
    this.metricContext.next(context);
  }

  updateNodeIs(nodeIds: number[]): void {
    const context = this.metricContext.value;
    context.sensorNodeIds = nodeIds;
    this.metricContext.next(context);
  }

  getCurrentContextDataType(): DataType {
    return this.metricContext.value.dataType;
  }

  updateContextBuilding(buildingId: number, floorId: number): void {
    if (buildingId !== this.metricContext.value.buildingId || floorId !== this.metricContext.value.floorIds[0]) {
      const newContext: IQueryContext = {
        ...this.metricContext.value,
        buildingId,
        floorIds: [floorId]
      };
      this.metricContext.next(newContext);
    }
  }

  updateContextDateAndResolution(dates: Dates, resolution: Resolution): void {
    const newContext = {
      ...this.metricContext.value,
      dates,
      resolution
    };
    this.metricContext.next(newContext);
  }

  updateContextFromUrl(dates: Dates, resolution: Resolution, tagIds: number[], dataType: DataType): void {
    const newContext = {
      ...this.metricContext.value,
      dates,
      resolution,
      tagIds,
      dataType
    };
    this.metricContext.next(newContext);
  }

  updateContextDataType(dataType: DataType): void {
    const newContext = {
      ...this.metricContext.value,
      dataType
    };
    this.metricContext.next(newContext);
  }

  getNavigationOutline(): ComplexQueryOutline {
    return new QueryOutlineBuilder(this.metricContext.value).buildNavigationOutline();
  }

  getMainOutline(): ComplexQueryOutline {
    return new QueryOutlineBuilder(this.metricContext.value).buildMainOutline();
  }

  getLiveOutline(): LiveQueryOutline {
    return new QueryOutlineBuilder(this.metricContext.value).buildLiveOutline();
  }

  updateQueryParams(): void {
    const context = this.currentContext;
    const scaleValues = ['yearly', 'monthly', 'daily', 'hourly'];
    const currentParams = { ...this.route.snapshot.queryParams };

    const { year, month, day, hour, minute, scale, tag, type, ...restParams } = currentParams;
    restParams.scale = context.resolution.toString().toLowerCase();
    const index = scaleValues.indexOf(restParams.scale);

    restParams.year = context.dates.years;
    if (index >= 1 || (index === 0 && context.dates.months.length > 0)) {
      restParams.month = context.dates.months;
    }
    if (index >= 2 || (index === 1 && context.dates.days.length > 0)) {
      restParams.day = context.dates.days;
    }
    if (index >= 3 || (index === 2 && context.dates.hours.length > 0)) {
      restParams.hour = context.dates.hours;
    }
    if (index == 3 && context.dates.minutes.length > 0) {
      restParams.minute = context.dates.minutes;
    }

    restParams.t = context.tagIds;
    restParams.type = context.dataType.toString().toLowerCase();

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: restParams
    });
  }
}
